import React, { useRef, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { PPWDWidgetBonusTotal } from '../PPDWidgetBonusTotal';
import { PPDModal } from '../../../PPDModal';
import { PPDWidgetPercentGrid } from '../PPDWidgetPercentGrid';
import { useStore, useOnClickOutside, useMedia } from 'shared/hooks';
import { formatValueWithUnits } from 'pages/profile-dashboard/utilities/formatValueWithUnits';
import { UiIcon, UiTooltip } from 'shared/uikit';
import './styles.scss';
import { IUserDashboardBonusGroup,
    IUserDashboardBonusItem,
    IUserDashboardBonusTotal,
    IUserDashboardLabels, } from 'shared/models';
import { ICONS } from 'shared/constants';
import { useDashboardDict } from 'pages/profile-dashboard/context/useDashboardDict';
import { keys } from 'mobx';

type PPDWidgetBonusModalProps = {
    isOpen?: boolean;
    onClose?: () => void;
    groups?: IUserDashboardBonusGroup[];
    items?: IUserDashboardBonusItem[];
    total?: IUserDashboardBonusTotal;
    labels?: Record<string, string>;
};

type BonusGroupItemsProps = {
    groupItems: IUserDashboardBonusItem[]
}

const BonusGroupItems = observer(({ groupItems }: BonusGroupItemsProps) => {
    const { units } = useDashboardDict();
    const { isMobileOrTablet } = useMedia();

    const renderStatusIcon = (status: string, size: number) => {
        switch (status) {
            case 'locked':
                return <ICONS.LOCK_THIN size={size} color="#787B83" />;
            case 'previous':
                return <ICONS.CHECK_THIN size={size} color="#787B83" />;
            case 'completed':
                return <ICONS.CHECK_THIN size={size} color="#16A34A" />;
            default:
                return null;
        }
    };

    const filteredGroupItems = groupItems.filter(
        (groupItem) => groupItem.items && groupItem.items.length > 0
    );

    if (!filteredGroupItems.length) return null;

    return (
        <ul className="ppd-widget-bonus-modal__group-list">
            {filteredGroupItems.map((groupItem) => (
                <li key={groupItem.title || groupItem.group_id} className="ppd-widget-bonus-modal__group-item">
                    {groupItem.title && (
                        <span className="ppd-widget-bonus-modal__list-title">{groupItem.title}</span>
                    )}
                    <ul className="ppd-widget-bonus-modal__list">
                        {groupItem.items!.map((item) => (
                            <li
                                key={item.name}
                                className={classNames(
                                    'ppd-widget-bonus-modal__item',
                                    item.status ? `ppd-widget-bonus-modal__item--${item.status}` : ''
                                )}
                            >
                                <div className="ppd-widget-bonus-modal__item-group">
                                    <div className="ppd-widget-bonus-modal__item-group-box">
                                        <div className="ppd-widget-bonus-modal__item-wrap">
                                            {item.status && renderStatusIcon(item.status, isMobileOrTablet ? 16 : 18) && (
                                                <span className="ppd-widget-bonus-modal__item-icon">
                                                    {renderStatusIcon(item.status, isMobileOrTablet ? 16 : 18)}
                                                </span>
                                            )}
                                            <span className="ppd-widget-bonus-modal__item-name">{item.name}</span>
                                        </div>
                                        {
                                            item.sign && <span className="ppd-widget-bonus-modal__item-sign">{item.sign}</span>
                                        }
                                    </div>
                                </div>
                                <span className="ppd-widget-bonus-modal__item-value">
                                    {item.value && formatValueWithUnits(item.value, units) ? formatValueWithUnits(item.value, units) : "-"}
                                </span>
                            </li>
                        ))}
                    </ul>
                </li>
            ))}
        </ul>
    );
});


type BonusGroupProps = {
    group: IUserDashboardBonusGroup;
    groupItems: IUserDashboardBonusItem[];
    labels?: IUserDashboardLabels;
}

const BonusGroup = observer(({ group, groupItems, labels }: BonusGroupProps) => {
    if (!groupItems.length) return null;

    const { isMobileOrTablet } = useMedia()

    const DEFAULT_TOOLTIP_WIDTH = 300;
    const MAX_TOOLTIP_WIDTH = isMobileOrTablet ? 344 : 482;

    const tooltipMaxWidth = useMemo(() => {
        if (!group.percent_grid?.items?.length) return DEFAULT_TOOLTIP_WIDTH;

        const texts: string[] = group.percent_grid.items.flatMap(item =>
            (keys(item) as string[])
                .map(k => (item as any)[k])
                .filter(v => typeof v === "string" || typeof v === "number")
                .map(String)
        );
        const longestText = texts.reduce((a, b) => (a.length > b.length ? a : b), "");

        if (longestText.length < 30) return DEFAULT_TOOLTIP_WIDTH;
        return MAX_TOOLTIP_WIDTH
    }, [group.percent_grid?.items, isMobileOrTablet]);

    const isTooltipWide = tooltipMaxWidth > DEFAULT_TOOLTIP_WIDTH;

    return (
        <div className="ppd-widget-bonus-modal__group">
            <div className="ppd-widget-bonus-modal__group-title">{group.name}</div>

            {group.percent_grid?.items && group.percent_grid.items.length > 0 && (
                <UiTooltip position="top" maxWidth={tooltipMaxWidth} content={<PPDWidgetPercentGrid group={group} labels={labels} isWide={isTooltipWide} />}>
                    <span className="ppd-widget-bonus-modal__tooltip-trigger">
                        <UiIcon icon={ICONS.HELP_CIRCLE} size={16} color="#175CE6" className="ppd-widget-bonus-modal__tooltip-icon"/>
                        {labels?.percent_grid}
                    </span>
                </UiTooltip>
            )}
            <BonusGroupItems groupItems={groupItems} />
        </div>
    );
});


export const PPDWidgetBonusModal = observer(({
    isOpen = false, onClose = () => {}, groups = [], items = [], total, labels = {}
}: PPDWidgetBonusModalProps ) => {
    const store = useStore(() => ({
        isOpenPercentGrid: false
    }));

    const tooltipRef = useRef<HTMLDivElement>(null);
    const handleCloseTooltip = useCallback(() => store.set('isOpenPercentGrid', false), [store]);

    useOnClickOutside(tooltipRef, handleCloseTooltip);

    const groupItemsMap = useMemo(() => {
        const map: Record<string, IUserDashboardBonusItem[]> = {};
        items.forEach((item) => {
            if (!map[item.group_id]) map[item.group_id] = [];
            map[item.group_id].push(item);
        });
        return map;
    }, [items]);

    return (
        <PPDModal className="ppd-widget-bonus-modal" isOpened={isOpen} onClose={onClose}>
            <div className="ppd-widget-bonus-modal__content">
                <div className="ppd-widget-bonus-modal__groups">
                    {groups.length > 0 ? (
                        groups.map((group) => (
                            <BonusGroup
                                key={group.id}
                                group={group}
                                groupItems={groupItemsMap[group.id] || []}
                                labels={labels}
                            />
                        ))
                    ) : (
                        <div className="ppd-widget-bonus-modal__empty">Бонусы отсутствуют</div>
                    )}
                </div>
                {total && (
                    <div className="ppd-widget-bonus-modal__total">
                        <PPWDWidgetBonusTotal
                            name={total.name}
                            value={total.value}
                            extra={total.extra || []}
                        />
                    </div>
                )}
            </div>
        </PPDModal>
    );
});
